import React, { useState, useEffect, useRef } from 'react'
import styles from './styles.module.scss'
import clsx from 'clsx'
import Image from 'next/image'
import { useAppIntl } from 'src/i18n'
import { useRouter } from 'next/router'

export type FilterProps = {
  keys?: string[]
  itemActive?: string
  setItemActive?: (item: string) => void
  search?: string
  setSearch?: (item: string) => void
}

export const Filter = ({
  keys,
  itemActive,
  setItemActive,
  search,
  setSearch,
}: FilterProps) => {
  const intl = useAppIntl()
  const [showSearch, setShowSearch] = useState(true)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedItem, setSelectedItem] = useState(itemActive || '')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { query } = router

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth
      setShowSearch(screenWidth > 1024)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown) {
        const dropdownContainer = document.querySelector(
          `.${styles.dropdownContainer}`
        )
        if (
          dropdownContainer &&
          !dropdownContainer.contains(event.target as Node)
        ) {
          setShowDropdown(false)
        }
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showDropdown])

  const handleCategoryClick = (key: string) => {
    setItemActive(key)
    setSelectedItem(key === '' ? intl.t('review.filter.allReviews') : key)

    if (key === '') {
      router.push({ query: { ...query, category: undefined } }, undefined, {
        shallow: true,
      })
    } else {
      router.push({ query: { ...query, category: key } }, undefined, {
        shallow: true,
      })
    }

    setShowDropdown(false)
  }

  const handleScrollLeft = () => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.scrollLeft -= 50
    }
  }

  const handleScrollRight = () => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.scrollLeft += 50
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const handleCloseSearch = () => {
    setSearch('')
    setItemActive('')
    setSelectedItem(intl.t('review.filter.allReviews'))
    setShowSearch(false)
    router.push({ query: { ...query, search: undefined } }, undefined, {
      shallow: true,
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.lettersBar}>
          <div className={styles.letters}>
            {showSearch && (
              <div
                className={clsx(styles.item, {
                  [styles.itemActive]: '' === itemActive,
                  [styles.mobileItem]: !showSearch,
                })}
                onClick={() => handleCategoryClick('')}
              >
                <div
                  className={clsx(styles.letter, {
                    [styles.letterActive]: '' === itemActive,
                  })}
                >
                  {intl.t('review.filter.allReviews')}
                </div>
              </div>
            )}
            <div className={styles.arrowButton} onClick={handleScrollLeft}>
              <Image
                src='/assets/icons/icon-arrow-prev.svg'
                width='17'
                height='17'
                alt='scroll left'
              />
            </div>

            <div className={styles.scrollContainer} ref={scrollContainerRef}>
              <div className={styles.scrollContent}>
                {keys?.map((key, index) => (
                  <div
                    key={index}
                    className={clsx(styles.itemSecond, {
                      [styles.itemSecondActive]: key === itemActive,
                    })}
                    onClick={() => handleCategoryClick(key)}
                  >
                    <div
                      className={clsx(styles.letter, {
                        [styles.letterActive]: key === itemActive,
                      })}
                    >
                      {key.toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.arrowButton} onClick={handleScrollRight}>
              <Image
                src='/assets/icons/icon-arrow-next.svg'
                width='17'
                height='17'
                alt='scroll right'
              />
            </div>

            {/* Dropdown Menu */}
            <div className={styles.dropdownContainer}>
              <div
                className={clsx(styles.itemSecond, styles.dropdownToggle, {
                  [styles.itemActive]: showDropdown,
                })}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <div
                  className={clsx(styles.letter, {
                    [styles.letterActive]: showDropdown,
                  })}
                >
                  {selectedItem.toUpperCase()}
                  <div className={styles.arrowIcon}>
                    <Image
                      src='/assets/icons/icon-arrow-down-new.svg'
                      width='17'
                      height='17'
                      alt='search icon'
                    />
                  </div>
                </div>
              </div>
              {showDropdown && (
                <div className={styles.dropdownContent}>
                  <div
                    className={clsx(styles.dropdownItem, {
                      [styles.itemSecondActive]: '' === itemActive,
                    })}
                    onClick={() => handleCategoryClick('')}
                  >
                    <div
                      className={clsx(styles.letter, {
                        [styles.letterActive]: '' === itemActive,
                      })}
                    >
                      {intl.t('review.filter.allReviews')}
                    </div>
                  </div>
                  {keys?.map((key, index) => (
                    <div
                      key={index}
                      className={styles.dropdownItem}
                      onClick={() => {
                        handleCategoryClick(key)
                        setShowDropdown(false)
                      }}
                    >
                      {key.toUpperCase()}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              className={clsx(styles.itemSearch, {
                [styles.itemActive]: showSearch,
              })}
              onClick={() => setShowSearch(!showSearch)}
            >
              <div
                className={clsx(styles.letter, {
                  [styles.letterActive]: showSearch,
                })}
              >
                <Image
                  src='/assets/icons/icon-search-white.svg'
                  width={17}
                  height={17}
                  alt='search icon'
                />
              </div>
            </div>
            <div className={styles.searchContainer}>
              {showSearch && (
                <div className={styles.inputContainer}>
                  <div className={styles.searchIcon}>
                    <Image
                      src='/assets/icons/icon-search-green.svg'
                      width='17'
                      height='17'
                      alt='search icon'
                    />
                  </div>
                  <input
                    type={'text'}
                    className={styles.input}
                    value={search}
                    onChange={handleChange}
                    placeholder={'Search through reviews...'}
                  />
                  <div className={styles.closeIcon} onClick={handleCloseSearch}>
                    <Image
                      className={styles.closeIcon}
                      src='/assets/icons/icon-close-green.svg'
                      width='17'
                      height='17'
                      alt='close icon'
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
